<template>
  <div>
    <account-modules-configuration class="mb-4 mx-lg-0" />
    <b-card class="main mb-4">
      <header slot="header">
        <b-row>
          <b-col :title="labels.payment" md="12">
            {{ labels.payment }}
          </b-col>
        </b-row>
      </header>
      <div class="side">
        <div class="side-top" v-if="isEnterprisePlan">
          <div class="credit-card" v-if="isCreditCardMethod">
            <CreditCard :card="paymentMethod" />
            <b-link
              class="mdi mdi-pencil alter text-purple05"
              @click="$refs.alterCardModal.show()"
            >
              {{ labels.alter }}
            </b-link>
          </div>
          <div class="boleto" v-if="isBoletoMethod">
            <Boleto />
          </div>
          <br />
          <hr />
        </div>
        <div>
          <div class="flex-between">
            <div>
              <h3 class="text-primary">{{ labels.licenses }}</h3>
              <span class="text-septenary">
                {{ labels.youHave }}
                <b>{{ billing.availableLicenses }} {{ labels.licenses }}</b>
              </span>
            </div>
            <small>
              <b-link
                class="mdi mdi-pencil alter text-purple05"
                v-if="isEnterprisePlan"
                @click="updateLicensesModal"
              >
                {{ labels.edit }}
              </b-link>
            </small>
          </div>
          <dl>
            <div>
              <dt>{{ labels.plan }}</dt>
              <dd>{{ plan }}</dd>
            </div>

            <div>
              <dt>{{ labels.billing }}</dt>
              <dd>{{ period }}</dd>
            </div>

            <div>
              <dt>{{ labels.initialDate }}</dt>
              <dd>{{ initialDate }}</dd>
            </div>

            <div>
              <dt>{{ labels.nextCharge }}</dt>
              <dd v-if="nextCharge">{{ nextCharge }}</dd>
              <dd v-if="!nextCharge"><i class="mdi mdi-infinity"></i></dd>
            </div>

            <div>
              <dt>{{ labels.pricePerUser }}</dt>
              <dd :class="{ 'text-lima': !isEnterprisePlan }">
                R$ {{ pricePerUser | toCurrency }}
              </dd>
            </div>

            <div>
              <dt>{{ labels.total }}</dt>
              <dd :class="{ 'text-lima': !isEnterprisePlan }">
                R$ {{ totalAmount | toCurrency }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
      <div class="side flex">
        <div class="side-top">
          <h3>{{ labels.clientSince }}</h3>
          <h4 class="flex-between">
            <span>{{ sinceDate }}</span>
            <small class="text-right">
              <!-- <b-link class="text-purple05">{{labels.paymentHistory}}</b-link> -->
            </small>
          </h4>
          <hr />
        </div>
        <div class="flex-grow" v-if="isEnterprisePlan"></div>
        <div class="pt-4 pb-4" v-if="!isEnterprisePlan">
          <h5>{{ labels.updateMyPlan }}</h5>
          <p>
            {{ labels.basicDescription1 }}
            <br />
            {{ labels.basicDescription2 }}
            <br />
            <br />
            <b-button variant="red02" @click="openAccountPlans()">
              {{ labels.getBusinessPlan }}
            </b-button>
          </p>
        </div>
        <div class="text-right">
          <hr />
          <b-link
            class="text-purple05"
            v-if="isEnterprisePlan"
            @click="$refs.cancelPlanModal.show()"
          >
            {{ labels.cancelAccount }}
          </b-link>
        </div>
      </div>
    </b-card>
    <div v-watch-menu-state>
      <b-modal
        id="updateLicensesModal"
        v-if="isEnterprisePlan"
        ref="updateLicensesModal"
        size="lg"
        :title="ucFirst(labels.licenses)"
        hide-footer
        :visible="openModal"
        header-bg-variant="light"
      >
        <LicensesUpdateForm @updateSuccess="onUpdateLisenses" />
      </b-modal>
      <b-modal
        id="updateLicensesModalSuccess"
        v-if="isEnterprisePlan"
        ref="updateLicensesModalSuccess"
        size="lg"
        :title="ucFirst(labels.licenses)"
        hide-footer
        header-bg-variant="light"
      >
        <ChargingSuccess />
      </b-modal>
      <b-modal
        id="cancelPlanModal"
        v-if="isEnterprisePlan"
        ref="cancelPlanModal"
        :title="labels.cancelAccount"
        hide-footer
        header-bg-variant="light"
      >
        <CancelPlan />
      </b-modal>
      <b-modal
        id="alterCardModal"
        v-if="isCreditCardMethod"
        ref="alterCardModal"
        :title="labels.alterCard"
        hide-footer
        header-bg-variant="light"
      >
        <form ref="formAlterCard">
          <IuguCardForm
            :showDocAndName="false"
            :form="cardInfo"
            :validate="validate"
            ref="iuguCardForm"
          />
          <div class="text-right">
            <b-button
              variant="red02"
              :disabled="loading"
              @click="alterCard()"
              >{{ labels.alter }}</b-button
            >
          </div>
        </form>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import IuguCardForm from '@/components/Payment/IuguCardForm.vue'
import CreditCard from '@/components/Shared/CreditCard.vue'
import Boleto from '@/components/Shared/Boleto.vue'
import CancelPlan from '@/components/Shared/CancelPlan.vue'
import { isEnterprisePlan } from '@/service/user.js'
import { alterCard } from '@/service/account'
import LicensesUpdateForm from '@/views/Home/Account/LicensesUpdateForm.vue'
import ChargingSuccess from '@/views/Home/Account/ChargingSuccess.vue'
import { createPaymentToken } from '@/service/iugu'
import { PERIODS } from '@/store/modules/account'
import { HIGH } from '@/helpers/messages-priority'
import AccountModulesConfiguration from '@/components/Home/Account/AccountModulesConfiguration.vue'

function getBreadcrumb(labels) {
  const breadcrumb = [
    {
      label: labels.account,
    },
  ]
  return breadcrumb
}

function cleanCard() {
  return {
    cardName: '',
    cardNumber: '',
    cardValidate: '',
    cvv: '',
    split: '',
  }
}

export default {
  components: {
    CreditCard,
    LicensesUpdateForm,
    ChargingSuccess,
    Boleto,
    CancelPlan,
    IuguCardForm,
    AccountModulesConfiguration,
  },

  data() {
    return {
      classFix: null,
      openModal: true,
      loading: false,
      validate: false,
      cardInfo: {
        cardName: '',
        cardNumber: '',
        cardValidate: '',
        cvv: '',
        split: '',
      },
    }
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      since: 'account/since',
      billing: 'account/billing',
      paymentMethod: 'account/paymentMethod',
      isCreditCardMethod: 'account/isCreditCardMethod',
      isBoletoMethod: 'account/isBoletoMethod',
      licensesPlan: 'account/licensesPlan',
    }),

    isEnterprisePlan: function() {
      return isEnterprisePlan()
    },

    labels: function() {
      return Object.assign(
        {},
        this.resources.general.menu,
        this.resources.general.message,
        this.resources.page.home.accountGeneric,
        this.resources.page.home.accountDetail
      )
    },

    sinceDate: function() {
      return this.since ? this.$moment(this.since).format('MMMM, YYYY') : '-'
    },

    plan: function() {
      return this.billing.plan.name
    },

    period: function() {
      if (!this.billing.period) return this.labels[PERIODS.MONTHLY]

      return this.billing.period in this.labels
        ? this.labels[this.billing.period]
        : this.labels[PERIODS.MONTHLY]
    },

    pricePerUser: function() {
      return 'pricePerUser' in this.billing ? this.billing.pricePerUser : 0
    },

    totalAmount: function() {
      return 'totalAmount' in this.billing ? this.billing.totalAmount : 0
    },

    initialDate: function() {
      return this.$moment(this.billing.initialDate).format('DD/MM/YYYY') || '-'
    },

    nextCharge: function() {
      return this.billing.expiresAt
        ? this.$moment(this.billing.expiresAt)
            .add(1, 'day')
            .format('DD/MM/YYYY')
        : undefined
    },
  },

  mounted() {
    const breadcrumb = getBreadcrumb(this.labels)
    this.$store.dispatch('account/loadConfirmURL')
    this.$store.dispatch('account/loadSubscriptions')
    this.$root.$emit('set-breadcrumb', breadcrumb)
    this.$root.$emit('setPageTitle', { context: this, path: 'account' })
  },

  methods: {
    validateFields() {
      this.validate = true
      if (!this.$refs.formAlterCard.checkValidity()) {
        throw new Error(this.labels.requiredFields)
      }

      if (!this.$refs.iuguCardForm.isValid) {
        throw new Error(this.labels.wrongFields)
      }
    },

    async alterCard() {
      try {
        this.loading = true
        this.validateFields()
        const response = await createPaymentToken(this.cardInfo) // IUGO
        if (response.errors) throw new Error('INVALID_CARD')
        await alterCard(response) // API
        this.onAlterCardSuccess()
      } catch (error) {
        this.onPurchaseError(error)
      }
      this.loading = false
    },

    onAlterCardSuccess() {
      this.$store.dispatch('account/loadSubscriptions')
      this.$root.$emit('app:message:show', {
        text: this.labels.cardUpdated,
        status: 'success',
        priority: HIGH,
      })
      this.$refs.alterCardModal.hide()
      this.$root.$emit('track', {
        event: 'Atualizou cartão',
        data: this.cardInfo,
      })
      this.cardInfo = cleanCard()
    },

    onPurchaseError(error) {
      if (error.message === 'INVALID_CARD') {
        error.message = this.labels.invalidCard
      }

      this.$root.$emit('error', error)

      this.$root.$emit('app:message:show', {
        text: error.message,
        status: 'danger',
        priority: HIGH,
      })
    },

    updateLicensesModal() {
      if (this.isBoletoMethod) this.setCustomPlanData()
      this.$refs.updateLicensesModal.show()
    },

    setCustomPlanData() {
      this.$store.commit('licensesUpdateForm/UPDATE_DATA', {
        partialPrice: 0,
        originalPrice: this.billing.pricePerUser,
        period: this.period,
        nextCharge: this.billing.expiresAt,
      })
    },

    onUpdateLisenses() {
      this.$store.dispatch('account/loadSubscriptions')
      this.$refs.updateLicensesModal.hide()
      this.$refs.updateLicensesModalSuccess.show()
    },

    ucFirst(str) {
      return (str[0] || '').toUpperCase().concat(str.slice(1))
    },

    openAccountPlans() {
      this.$router.push({ name: 'account.plans' })
      this.$root.$emit('track', {
        event: 'Clicou em obter ver planos',
        data: {
          category: 'Upgrade',
        },
      })
    },
  },

  beforeMount() {
    this.openModal =
      this.$route.query.updateLicenses !== undefined && isEnterprisePlan()
  },
}
</script>

<style lang="scss" scoped>
@import '~@/styles/custom/colors';

.main {
  .card-body {
    display: flex;
    margin: 3rem 0;
    padding: 0;

    @media (max-width: '425px') {
      flex-wrap: wrap;
      margin: 1rem 0;
    }

    .flex-grow {
      flex-grow: 1;
    }

    .flex-between {
      display: flex;
      justify-content: space-between;
      font-size: 1.3rem;
    }

    .side {
      flex-basis: 50%;
      padding: 0 2rem;

      &.flex {
        display: flex;
        flex-direction: column;
      }

      @media (max-width: '425px') {
        flex-basis: 100%;
      }

      .credit-card {
        display: flex;
        justify-content: space-between;

        @media (max-width: '425px') {
          align-items: flex-end;
          flex-direction: column-reverse;
        }

        .alter {
          width: 70px;
        }
      }

      .side-top {
        min-height: 100px;
      }

      &:first-child {
        border-right: 1px solid map-get($theme-colors, 'gray07');

        @media (max-width: '425px') {
          border-right: 0;
        }
      }

      h4 {
        color: map-get($theme-colors, 'gray03');
        text-transform: capitalize;
        margin: 0;
      }

      h3 {
        color: map-get($theme-colors, 'primary');
        font-size: 18px;
        font-weight: 500;
        margin: 0;
        text-transform: uppercase;
      }

      h5 {
        color: map-get($theme-colors, 'primary');
        font-size: 28px;
      }

      p {
        color: map-get($theme-colors, 'gray03');

        .btn-red2 {
          font-size: medium;
          font-weight: 500;
          padding: 6px 25px;
        }
      }

      dl {
        div {
          display: flex;
          justify-content: space-between;
          margin: 10px 0;

          dt {
            color: map-get($theme-colors, 'gray02');
            font-size: 18px;
            font-weight: 100;
          }

          dd {
            color: map-get($theme-colors, 'primary');
            font-weight: 500;
          }
        }
      }
    }
  }
}

.card {
  .card-header {
    font-size: 24px;
  }
}
</style>

<style lang="scss">
.fix-175 .modal-dialog {
  @media (min-width: 992px) {
    transform: translateX(175px) !important;
  }
}

.fix-33 .modal-dialog {
  transform: translateX(33px) !important;
}

.modal-button-close {
  margin-top: -5px;
  height: 40px !important;
  width: 40px;
  border: none;
  box-shadow: 0 3px 6px rgba(61, 90, 254, 0.6);

  &::before {
    font-weight: 500;
    color: #573d55;
  }
}
</style>
